import { darken, lighten } from '@mui/material';

/** Values derived from the palette that all color helpers need */
export type TimelineColorContext = {
    isDark: boolean;
    primaryMain: string;
    /** palette.grey[200] */
    greyLight: string;
    /** palette.grey[700] */
    greyDark: string;
    /** neutral color for inactive connectors/dots */
    inactiveColor: string;
};

/**
 * Background color for the halo ring behind a step dot.
 * - Done steps  → very light primary (or darkened in dark mode)
 * - Current step → medium primary
 * - Future steps → neutral grey
 */
export const getHaloColor = (
    index: number,
    currentIndex: number,
    ctx: TimelineColorContext,
): string => {
    if (index > currentIndex) {
        return ctx.isDark ? ctx.greyDark : ctx.greyLight;
    }
    if (index === currentIndex) {
        return ctx.isDark ? darken(ctx.primaryMain, 0.4) : lighten(ctx.primaryMain, 0.6);
    }
    return ctx.isDark ? darken(ctx.primaryMain, 0.6) : lighten(ctx.primaryMain, 0.85);
};

/** Fill color for the inner dot — primary when active, neutral otherwise */
export const getDotColor = (
    index: number,
    currentIndex: number,
    ctx: TimelineColorContext,
): string => {
    if (index <= currentIndex) return ctx.primaryMain;
    return ctx.inactiveColor;
};

/** Color for a connector segment — primary if traversed, neutral otherwise */
export const getConnectorColor = (isDone: boolean, ctx: TimelineColorContext): string => {
    if (isDone) return ctx.primaryMain;
    return ctx.inactiveColor;
};

/**
 * Horizontal alignment of a step's content in desktop layout.
 * first=left · last=right · others=center
 */
export const getDesktopAlignment = (
    index: number,
    stepsLength: number,
): 'flex-start' | 'center' | 'flex-end' => {
    if (index === 0) return 'flex-start';
    if (index === stepsLength - 1) return 'flex-end';
    return 'center';
};
